import {
  Box,
  ButtonVariants,
  CustomInstrumentRequestStatusEnum,
  IconName,
  NotificationVariants,
  POST,
  ProductTypeEnum,
  Text,
  WorkflowCompactSummaryCard,
  WorkflowFormSteps,
  WorkflowSuccessView,
  createWorkflowComponent,
  request,
  useAppwideDrawerContext,
  useCurrenciesContext,
  useGlobalToasts,
  useSecuritiesContext,
  useUserContext,
  useWorkflowCopy,
  type ICustomInstrumentRequestResponse,
  type SubscriptionResponse,
  type WorkflowStepDefinition,
  type WorkflowSummaryRow,
  type WorkflowSummarySection,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { getInstrumentDetailsDrawerOptions, getSetupDrawerOptions } from './customSecurityWorkflowFields';
import type {
  CreateCustomSecurityForm,
  CreateCustomSecurityStepId,
  CustomInstrumentCreateResult,
} from './customSecurityWorkflowTypes';
import { CopyFromListedSymbol } from './customSecurityWorkflowTypes';
import {
  DEFAULT_CREATE_CUSTOM_SECURITY_FORM,
  buildCreateCustomInstrumentRequest,
  getCustomInstrumentErrorMessage,
  getFormWithCopiedSecurityFields,
  getFormWithoutCopiedSecurityFields,
  getProductTypeLabel,
  parseCustomInstrumentCreateResult,
} from './customSecurityWorkflowUtils';

function removeEmptyRows(rows: WorkflowSummaryRow[]): WorkflowSummaryRow[] {
  return rows.filter(row => row.value != null && row.value !== '');
}

function CreateCustomSecurityWorkflowComponent() {
  const { closeDrawer } = useAppwideDrawerContext();
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useUserContext();
  const { securitiesList, securitiesBySymbol } = useSecuritiesContext();
  const { currenciesList } = useCurrenciesContext();
  const { copySections } = useWorkflowCopy();

  const [currentStep, setCurrentStep] = useState<CreateCustomSecurityStepId>('setup');
  const [form, setForm] = useState<CreateCustomSecurityForm>(DEFAULT_CREATE_CUSTOM_SECURITY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [createResult, setCreateResult] = useState<CustomInstrumentCreateResult>();

  const setupDrawerOptions = useMemo(
    () => getSetupDrawerOptions({ securitiesList, securitiesBySymbol }),
    [securitiesBySymbol, securitiesList]
  );

  const instrumentDetailsDrawerOptions = useMemo(
    () => getInstrumentDetailsDrawerOptions({ currenciesList, securitiesBySymbol }),
    [currenciesList, securitiesBySymbol]
  );

  const handleFormStateChange = useCallback((updatedForm: CreateCustomSecurityForm) => {
    setForm(prev => ({ ...prev, ...updatedForm }));
  }, []);

  const handleSetupSave = useCallback(
    (preparedForm: CreateCustomSecurityForm) => {
      setSubmitError(undefined);
      setForm(prev => {
        const mergedForm = {
          ...prev,
          ...preparedForm,
          CopySourceSymbol:
            preparedForm.CopyFromListedSymbol === CopyFromListedSymbol.Yes ? preparedForm.CopySourceSymbol : undefined,
        };

        if (preparedForm.CopyFromListedSymbol === CopyFromListedSymbol.No) {
          return getFormWithoutCopiedSecurityFields(mergedForm);
        }

        const selectedSecurity = mergedForm.CopySourceSymbol
          ? securitiesBySymbol.get(mergedForm.CopySourceSymbol)
          : undefined;

        if (selectedSecurity) {
          return getFormWithCopiedSecurityFields(mergedForm, selectedSecurity);
        }

        // Product type changed — reset copied fields so stale data doesn't leak across types.
        if (prev.ProductType && prev.ProductType !== preparedForm.ProductType) {
          return getFormWithoutCopiedSecurityFields({
            ...mergedForm,
            ProductType: preparedForm.ProductType,
          });
        }

        return mergedForm;
      });
      setCurrentStep('instrumentDetails');
    },
    [securitiesBySymbol]
  );

  const handleSelectStep = useCallback((stepId: CreateCustomSecurityStepId) => {
    setSubmitError(undefined);
    setCurrentStep(stepId);
  }, []);

  const handleSubmit = useCallback(
    async (preparedForm: CreateCustomSecurityForm) => {
      const mergedForm = { ...form, ...preparedForm };
      setForm(mergedForm);
      setSubmitError(undefined);

      if (!orgApiEndpoint) {
        const message = 'Missing organization API endpoint';
        setSubmitError(message);
        addToast({ text: message, variant: NotificationVariants.Negative });
        return;
      }

      setIsSubmitting(true);
      try {
        const body = buildCreateCustomInstrumentRequest(mergedForm);
        const response = await request<SubscriptionResponse<ICustomInstrumentRequestResponse, 'CustomInstrument'>>(
          POST,
          `${orgApiEndpoint}/custom-instruments`,
          body
        );
        const result = parseCustomInstrumentCreateResult(response, mergedForm.ProductType!);
        setCreateResult(result);
        setCurrentStep('success');
        addToast({
          text:
            result.status === CustomInstrumentRequestStatusEnum.LookedUp
              ? `Custom security ${result.symbol} already exists.`
              : `Custom security ${result.symbol} created.`,
          variant:
            result.status === CustomInstrumentRequestStatusEnum.LookedUp
              ? NotificationVariants.Warning
              : NotificationVariants.Positive,
        });
      } catch (error) {
        const message = getCustomInstrumentErrorMessage(error);
        setSubmitError(message);
        addToast({ text: message, variant: NotificationVariants.Negative });
      } finally {
        setIsSubmitting(false);
      }
    },
    [addToast, form, orgApiEndpoint]
  );

  const instrumentSummaryRows = useMemo(
    (): WorkflowSummaryRow[] => [
      { label: 'Product Type', value: getProductTypeLabel(form.ProductType!) },
      {
        label: 'Copy Source',
        value: form.CopySourceSymbol
          ? (securitiesBySymbol.get(form.CopySourceSymbol)?.DisplaySymbol ?? form.CopySourceSymbol)
          : undefined,
      },
    ],
    [form.CopySourceSymbol, form.ProductType, securitiesBySymbol]
  );

  const instrumentSummary = useMemo(
    () => (
      <>
        <WorkflowCompactSummaryCard rows={instrumentSummaryRows} />
        {submitError ? (
          <Box mt="spacing12">
            <Text color="colorTextNegative">{submitError}</Text>
          </Box>
        ) : null}
      </>
    ),
    [instrumentSummaryRows, submitError]
  );

  const successSummarySections = useMemo((): WorkflowSummarySection[] => {
    if (!createResult) {
      return [];
    }
    return [
      {
        title: 'Custom Security',
        rows: removeEmptyRows([
          { label: 'Symbol', value: createResult.symbol },
          { label: 'Display Symbol', value: form.DisplaySymbol },
          { label: 'Product Type', value: getProductTypeLabel(createResult.productType) },
          { label: 'Status', value: createResult.status },
        ]),
      },
      {
        title: 'Market & Currency',
        rows: removeEmptyRows([
          { label: 'Market', value: form.Market },
          { label: 'Base Currency', value: form.BaseCurrency },
          { label: 'Quote Currency', value: form.QuoteCurrency },
          { label: 'Position Currency', value: form.PositionCurrency },
          { label: 'Settlement Currency', value: form.SettlementCurrency },
          { label: 'Settle Type', value: form.SettleValueType },
        ]),
      },
      {
        title: 'Display & Increments',
        rows: removeEmptyRows([
          { label: 'Description', value: form.Description },
          { label: 'Default Size Increment', value: form.DefaultSizeIncrement },
          { label: 'Min Size Increment', value: form.MinSizeIncrement },
          { label: 'Min Price Increment', value: form.MinPriceIncrement },
          { label: 'Default Price Increment', value: form.DefaultPriceIncrement },
          { label: 'Contract Size', value: form.ContractSize },
          { label: 'Price Display Spec', value: form.PriceDisplaySpec },
          { label: 'Size Display Spec', value: form.SizeDisplaySpec },
          { label: 'Notional Size', value: form.NotionalSize },
        ]),
      },
      {
        title: 'Copy Source',
        rows: removeEmptyRows([
          {
            label: 'Listed Symbol',
            value: form.CopySourceSymbol
              ? (securitiesBySymbol.get(form.CopySourceSymbol)?.DisplaySymbol ?? form.CopySourceSymbol)
              : undefined,
          },
        ]),
      },
      {
        title: form.ProductType === ProductTypeEnum.Option ? 'Option Details' : 'Expiration Details',
        rows: removeEmptyRows([
          { label: 'Expiry', value: form.Expiry?.toISOString() },
          { label: 'Strike Price', value: form.StrikePrice },
          { label: 'Option Type', value: form.OptionType },
          { label: 'Underlying Quote Currency', value: form.UnderlyingQuoteCurrency },
        ]),
      },
      ...(createResult.status === CustomInstrumentRequestStatusEnum.LookedUp
        ? [
            {
              title: 'Note',
              rows: [{ label: 'Result', value: 'A matching custom security already existed.' }],
            },
          ]
        : []),
    ];
  }, [createResult, form, securitiesBySymbol]);

  const handleCopySuccessDetails = useCallback(() => {
    copySections(successSummarySections, 'Custom security details copied.');
  }, [copySections, successSummarySections]);

  if (currentStep === 'success') {
    return (
      <WorkflowSuccessView
        message={
          createResult?.status === CustomInstrumentRequestStatusEnum.LookedUp
            ? 'A matching custom security was found.'
            : 'Custom security was successfully created.'
        }
        sections={successSummarySections}
        onCopyDetails={handleCopySuccessDetails}
        onDone={closeDrawer}
      />
    );
  }

  const steps: WorkflowStepDefinition<CreateCustomSecurityForm, CreateCustomSecurityStepId>[] = [
    {
      id: 'setup',
      label: 'Setup',
      icon: IconName.Cube,
      drawerOptions: setupDrawerOptions,
      onSave: handleSetupSave,
    },
    {
      id: 'instrumentDetails',
      label: 'Details',
      icon: IconName.Pencil,
      drawerOptions: instrumentDetailsDrawerOptions,
      summary: instrumentSummary,
      loading: isSubmitting,
      saveLabel: 'Create Custom Security',
      saveVariant: ButtonVariants.Positive,
      onSave: handleSubmit,
    },
  ];

  return (
    <WorkflowFormSteps
      steps={steps}
      currentStepId={currentStep}
      form={form}
      onSelectStep={handleSelectStep}
      onFormStateChange={handleFormStateChange}
    />
  );
}

export const CreateCustomSecurityWorkflow = createWorkflowComponent(CreateCustomSecurityWorkflowComponent, {
  title: 'Create Custom Security',
  hasGuideText: true,
});
